import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { BaseModalType } from '@context/ModalsContext'

import { ListItem } from '@ui/components/List'
import { Modal } from '@ui/components/Modal'
import { ChartPieIcon } from '@ui/icons/ChartPie.icon'
import { FileIcon } from '@ui/icons/File.icon'
import { GraphIcon } from '@ui/icons/Graph.icon'
import { LinkIcon } from '@ui/icons/Link.icon'
import { MetricsIcon } from '@ui/icons/Metrics.icon'
import { MoneyIcon } from '@ui/icons/Money.icon'
import { UserGroupIcon } from '@ui/icons/UserGroup.icon'
import { WalletIcon } from '@ui/icons/Wallet.icon'
import { WavyBillIcon } from '@ui/icons/WavyBill.icon'

import { Box } from '@shared/components/Box'

import { WidgetType } from '../types'
import type { Widget } from '../types'

import styles from './WidgetPalette.modal.module.scss'

interface WidgetPaletteProps {
  onChange: (widgetTypes: WidgetType[]) => void
  onSave: (widgetTypes: WidgetType[]) => void
  existingWidgets: Widget[]
  availableWidgets: Widget[]
}

export const WidgetPalette: BaseModalType<WidgetPaletteProps> = ({
  onChange,
  onSave,
  existingWidgets,
  availableWidgets,
  close,
}) => {
  const { t } = useTranslation()
  const [selectedTypes, setSelectedTypes] = useState(
    () => new Set(existingWidgets.map(({ type }) => type))
  )

  const setWidgetSelected = (widgetType: WidgetType, isSelected: boolean) => {
    const next = new Set(selectedTypes)

    if (isSelected) {
      next.add(widgetType)
    } else {
      next.delete(widgetType)
    }

    setSelectedTypes(next)
    onChange([...next])
  }

  const handleSave = () => {
    onSave([...selectedTypes])
    close()
  }

  return (
    <Modal>
      <Modal.Header canClose>
        {existingWidgets.length === 0 ? t('Dashboard.AddWidget') : t('Dashboard.EditWidgets')}
      </Modal.Header>

      <Modal.Body>
        <Box fullWidth>
          <div className={styles.widgetsGrid}>
            {availableWidgets.map((widget) => {
              const isSelected = selectedTypes.has(widget.type)

              return (
                <ListItem
                  key={widget.id}
                  active={isSelected}
                  onClick={() => setWidgetSelected(widget.type, !isSelected)}
                  startSlot={{
                    icon: { icon: getWidgetIcon(widget.type), color: 'text-med-em' },
                    size: 'x40',
                    shape: 'squircle',
                    bg: 'surface-white-smoke-0',
                    border: 'outline-low-em',
                  }}
                  endSlot={{
                    toggle: {
                      name: 'dashboard-widget-' + widget.id,
                      value: isSelected,
                      onChange: (value) => setWidgetSelected(widget.type, value),
                    },
                  }}
                >
                  {widget.title}
                </ListItem>
              )
            })}
          </div>
        </Box>
      </Modal.Body>

      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary onClick={handleSave}>{t('Save')}</Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}

function getWidgetIcon(type: WidgetType) {
  switch (type) {
    case WidgetType.IB_WALLETS:
      return WalletIcon
    case WidgetType.IB_CJM:
      return ChartPieIcon
    case WidgetType.IB_METRICS:
      return MetricsIcon
    case WidgetType.REFERRAL_LINK:
      return LinkIcon
    case WidgetType.PAYOUT_SCHEME:
      return MoneyIcon
    case WidgetType.IB_REBATES_MANAGEMENT:
      return UserGroupIcon
    case WidgetType.LOYALTY_PROGRAM:
      return WavyBillIcon
    case WidgetType.QUICK_PERFORMANCE:
      return GraphIcon
    case WidgetType.IB_TRANSACTION_HISTORY:
      return FileIcon
  }
}
